import React, { useState, useEffect, useMemo, useCallback, useDeferredValue, useTransition } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Link,
  Button,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
  Checkbox,
  ListItemText,
  OutlinedInput,
  ListSubheader,
  Divider,
  Collapse,
  FormControlLabel,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import dayjs from 'dayjs';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './constants';
import { resolveProductImageUrlFromFields, getEffectiveBrandFromOverrides, indexOverridesByTitle, getOverrideValue, PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY, PRODUCT_FLAG_SKU_VISIBILITY, PRODUCT_FLAG_LABEL_SKU_VISIBILITY, SKU_VISIBILITY_OPTIONS, SKU_VISIBILITY_DEFAULT, normalizeSkuVisibility, getSkuVisibility, getSkuVisibilityLabel } from './productUtils';
import ProductDetailPopup from './ProductDetailPopup';
import DateRangeFilter from './DateRangeFilter';
import { Search as SearchIcon, Edit as EditIcon, Add as AddIcon, Remove as RemoveIcon, Flag as FlagIcon, OutlinedFlag as OutlinedFlagIcon, CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon, Verified as VerifiedIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, HelpOutline as HelpOutlineIcon, Settings as SettingsIcon, WarningAmber as WarningAmberIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { getFlatVariantsFromGroup, sanitizeVariant } from './matchingUtils';
import { useAuth } from './AuthContext';
import { getStyleTokens } from './productViewProjectConfig';
import useSkuTitleTranslations from './hooks/useSkuTitleTranslations';
import SkuTitleWithTranslate from './components/SkuTitleWithTranslate';

const ADMIN_EDIT_EMAIL = 'dominik@forloop.ai';
const FORLOOP_TEAM_FILTER_KEY = '__forloop_team__';
const NOT_REVIEWED_FILTER_KEY = '__not_reviewed__';
const NOT_REVIEWED_BY_EMAIL_FILTER_PREFIX = '__not_reviewed_by_email__:';
const REVIEWED_FILTER_KEY = '__reviewed__';
const UNCERTAIN_FILTER_KEY = '__uncertain__';
const UNCERTAIN_EMAIL_FILTER_PREFIX = '__uncertain_email__:';
const COLUMN_VISIBILITY_STORAGE_KEY = 'jti_products_overview_column_visibility_v3';
const DEFAULT_HIDDEN_COLUMNS = ['first_seen_date', 'last_seen_date', 'content'];
const ALWAYS_VISIBLE_COLUMNS = new Set(['title']);

const loadHiddenColumns = () => {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((field) => !ALWAYS_VISIBLE_COLUMNS.has(field)));
      }
    }
  } catch {
    // ignore invalid stored value
  }
  return new Set(DEFAULT_HIDDEN_COLUMNS);
};

const formatSeenDate = (ts) => (ts != null ? dayjs(ts).format('YYYY-MM-DD') : null);

const TICKET_STATUS_OPTIONS = ['Open', 'In Progress', 'Waiting for User', 'Resolved', 'Closed'];

const formatTicketTimestamp = (value) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : String(value);
};

function FlagTicketListItem({
  ticket,
  projectKey,
  canChangeStatus,
  discussionOpen,
  onToggleDiscussion,
  details,
  savingStatus,
  onStatusChange,
}) {
  const messages = details?.messages || [];
  const description = (ticket.message || details?.message || '').trim();
  const hasDiscussion = messages.length > 0 || details?.loading || Boolean(details?.error);

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Ticket #{ticket.id}
        </Typography>
        {canChangeStatus ? (
          <FormControl size="small" sx={{ minWidth: 140 }} onClick={(e) => e.stopPropagation()}>
            <Select
              value={ticket.status || 'Open'}
              onChange={(e) => onStatusChange(ticket.id, e.target.value)}
              disabled={savingStatus}
              sx={{ fontSize: '0.8rem', height: 28 }}
            >
              {TICKET_STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Chip
            size="small"
            label={ticket.status || 'Open'}
            color={getTicketStatusColor(ticket.status)}
          />
        )}
        {savingStatus && <CircularProgress size={14} />}
        <Link
          component={RouterLink}
          to={`/${projectKey}/support/${ticket.id}`}
          underline="hover"
          sx={{ fontWeight: 600, fontSize: '0.875rem', ml: 'auto' }}
        >
          View ticket
        </Link>
      </Box>
      <Box
        sx={{
          mt: 1,
          p: 1,
          borderRadius: 1,
          bgcolor: 'grey.50',
          whiteSpace: 'pre-wrap',
          fontSize: '0.8rem',
          color: 'text.primary',
        }}
      >
        {description || 'No description.'}
      </Box>
      {hasDiscussion && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            <Button
              size="small"
              onClick={onToggleDiscussion}
              endIcon={discussionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none', minWidth: 0, px: 0.75 }}
            >
              Discussion{messages.length > 0 ? ` (${messages.length})` : ''}
            </Button>
          </Box>
          <Collapse in={discussionOpen} unmountOnExit>
            <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'grey.50' }}>
              {details?.loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Loading discussion…
                  </Typography>
                </Box>
              ) : details?.error ? (
                <Typography variant="caption" color="error">
                  {details.error}
                </Typography>
              ) : (
                messages.map((msg) => (
                  <Box key={msg.id} sx={{ mb: 1.25, '&:last-child': { mb: 0 } }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                      {msg.author_name || 'Unknown'}
                      {msg.created_at ? (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 400 }}>
                          {formatTicketTimestamp(msg.created_at)}
                          {msg.edited_at ? ' (edited)' : ''}
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                      {msg.message || '—'}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
}

const uncertainEmailToFilterKey = (email) => `${UNCERTAIN_EMAIL_FILTER_PREFIX}${email}`;
const filterKeyToUncertainEmail = (key) =>
  String(key).startsWith(UNCERTAIN_EMAIL_FILTER_PREFIX)
    ? key.slice(UNCERTAIN_EMAIL_FILTER_PREFIX.length)
    : null;

const notReviewedEmailToFilterKey = (email) => `${NOT_REVIEWED_BY_EMAIL_FILTER_PREFIX}${email}`;
const filterKeyToNotReviewedEmail = (key) =>
  String(key).startsWith(NOT_REVIEWED_BY_EMAIL_FILTER_PREFIX)
    ? key.slice(NOT_REVIEWED_BY_EMAIL_FILTER_PREFIX.length)
    : null;

const isForloopEmail = (email) => String(email || '').toLowerCase().includes('forloop.ai');

const getTicketStatusColor = (status) => {
  const statusLower = (status || '').toLowerCase();
  if (statusLower === 'open') return 'warning';
  if (statusLower === 'in progress') return 'info';
  if (statusLower === 'waiting for user') return 'default';
  if (statusLower === 'resolved') return 'success';
  if (statusLower === 'closed') return 'default';
  return 'default';
};

const isClosedTicketStatus = (status) => {
  const s = String(status || '').toLowerCase();
  return s === 'resolved' || s === 'closed';
};

const getTicketDescriptionText = (ticket) => {
  const raw = String(ticket?.message || '').trim();
  if (!raw) return '';
  return raw.replace(/^Reported by:[^\n]*(?:\n+)?/i, '').trim();
};

const formatConcatenatedTicketDescriptions = (tickets) => {
  if (!tickets?.length) return '';
  const parts = tickets
    .map((ticket) => {
      const desc = getTicketDescriptionText(ticket);
      if (!desc) return null;
      return tickets.length > 1 ? `#${ticket.id}: ${desc}` : desc;
    })
    .filter(Boolean);
  return parts.join('\n\n');
};

const getFlagTicketTone = (tickets) => {
  if (!tickets?.length) return null;
  if (tickets.some((t) => !isClosedTicketStatus(t.status))) return 'pending';
  return 'resolved';
};

const groupFlagTicketsByTitle = (tickets) => {
  const map = {};
  (tickets || []).forEach((ticket) => {
    const title = ticket.product_title;
    if (!title) return;
    if (!map[title]) map[title] = [];
    map[title].push(ticket);
  });
  return map;
};

const getUncertainBy = (productFlags, title) => {
  const val = productFlags[title]?.uncertainBy;
  return Array.isArray(val) ? val : [];
};

const getIncorrectInfoReportedBy = (productFlags, title) => {
  const val = productFlags[title]?.incorrectInfoReportedBy;
  return Array.isArray(val) ? val : [];
};

/** Format bundle content array (allowing duplicates) as "Item (2), Other" */
function formatBundleContentWithCounts(arr) {
  if (!arr?.length) return '';
  const counts = {};
  arr.forEach((item) => {
    const k = String(item).trim();
    if (k) counts[k] = (counts[k] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([item, n]) => (n > 1 ? `${item} (${n})` : item))
    .join(', ');
}

const normalizeOverviewCountryKey = (value) =>
  (value || '').toString().trim().toLowerCase() || '__unknown__';

function overviewProductKey(country, title) {
  return `${normalizeOverviewCountryKey(country)}\x1f${title || ''}`;
}

function normalizeProductUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    parsed.hash = '';
    if (parsed.pathname.length > 1 && parsed.pathname.endsWith('/')) {
      parsed.pathname = parsed.pathname.slice(0, -1);
    }
    parsed.hostname = parsed.hostname.toLowerCase();
    return parsed.toString();
  } catch {
    return raw.replace(/\/+$/, '');
  }
}

function dateKeyFromOverviewRow(row) {
  if (row?.__ts != null) return dayjs(row.__ts).format('YYYY-MM-DD');
  const parsed = dayjs(row?.updated_at || row?.date);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

function getTitleOverrideRecord(url, overrides) {
  if (!url || !overrides) return null;
  const key = normalizeProductUrl(url);
  const rec = overrides[key] || overrides[url];
  if (!rec) return null;
  if (typeof rec === 'string') {
    const title = rec.trim();
    return title ? { title, original_title: '' } : null;
  }
  const title = String(rec.title || '').trim();
  if (!title) return null;
  return { title, original_title: String(rec.original_title || '').trim() };
}

function applyTitleOverridesToRows(rows, overrides) {
  if (!rows?.length || !overrides || !Object.keys(overrides).length) return rows;
  return rows.map((row) => {
    const rec = getTitleOverrideRecord(row.url, overrides);
    if (!rec?.title) return row;
    const previousTitle = row.original_title || row.title;
    return {
      ...row,
      original_title: rec.original_title || (previousTitle !== rec.title ? previousTitle : row.original_title) || previousTitle,
      title: rec.title,
    };
  });
}

function SkuDuplicateRenameDialog({
  open,
  product,
  titleOverrides,
  existingTitlesInCountry,
  savingUrl,
  onClose,
  onRename,
  onRevert,
}) {
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    if (!product) {
      setDrafts({});
      return;
    }
    const next = {};
    (product.listingUrls || []).forEach((entry) => {
      const rec = getTitleOverrideRecord(entry.url, titleOverrides);
      next[entry.url] = rec?.title || product.title || '';
    });
    setDrafts(next);
  }, [product, titleOverrides]);

  const listingUrls = product?.listingUrls || [];
  const duplicateDates = product?.duplicateDates || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {product?.hasDuplicateSkuTitle ? 'Duplicate SKU title' : 'Rename SKU title'}
      </DialogTitle>
      <DialogContent>
        {product ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {product.hasDuplicateSkuTitle
                ? 'This title appears with different listing URLs on the same date in the same country. Rename one URL globally so the two products are tracked separately.'
                : 'Rename this listing URL globally. The new title applies on every scrape date.'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {product.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Country: {(product.country || '').toUpperCase() || '—'}
              {duplicateDates.length > 0
                ? ` · Same-date collisions: ${duplicateDates.map((d) => d.date).join(', ')}`
                : ''}
            </Typography>
            {listingUrls.length === 0 ? (
              <Alert severity="info">No listing URLs found for this product.</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {listingUrls.map((entry) => {
                  const rec = getTitleOverrideRecord(entry.url, titleOverrides);
                  const draft = drafts[entry.url] ?? '';
                  const trimmed = draft.trim();
                  const isSaving = savingUrl === entry.url;
                  const collisionOnThisUrl = duplicateDates.some((d) => d.urls.includes(entry.url));
                  const titleClash =
                    trimmed &&
                    trimmed !== product.title &&
                    existingTitlesInCountry.includes(trimmed);
                  return (
                    <Box
                      key={entry.url}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: collisionOnThisUrl ? 'warning.main' : 'divider',
                        bgcolor: collisionOnThisUrl ? 'rgba(237, 108, 2, 0.08)' : 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Link
                          href={entry.displayUrl || entry.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{
                            fontSize: '0.8rem',
                            wordBreak: 'break-all',
                            flex: 1,
                          }}
                        >
                          {entry.displayUrl || entry.url}
                        </Link>
                        <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.25 }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {entry.retailers?.length ? `Retailer: ${entry.retailers.join(', ')}` : 'Retailer: —'}
                        {entry.dates?.length ? ` · Dates: ${entry.dates.length}` : ''}
                        {collisionOnThisUrl ? ' · Collides on a selected date' : ''}
                      </Typography>
                      {rec?.original_title && rec.original_title !== rec.title && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Original title: {rec.original_title}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                          size="small"
                          label="New global title"
                          value={draft}
                          onChange={(e) =>
                            setDrafts((prev) => ({ ...prev, [entry.url]: e.target.value }))
                          }
                          sx={{ flex: 1, minWidth: 220 }}
                          disabled={isSaving}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          disabled={
                            isSaving ||
                            !trimmed ||
                            trimmed === (rec?.title || product.title)
                          }
                          onClick={() =>
                            onRename(entry.url, trimmed, rec?.original_title || product.title)
                          }
                        >
                          {isSaving ? 'Saving…' : 'Rename globally'}
                        </Button>
                        {rec ? (
                          <Button
                            variant="outlined"
                            size="small"
                            color="inherit"
                            disabled={isSaving}
                            onClick={() => onRevert(entry.url)}
                          >
                            Revert
                          </Button>
                        ) : null}
                      </Box>
                      {titleClash && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Another product in this country already uses that title.
                        </Alert>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function productMatchesReviewFilters(product, filterKeys) {
  const reviewers = product._reviewersLower || [];
  const uncertainBy = product._uncertainByLower || [];
  return filterKeys.some((filterKey) => {
    if (filterKey === NOT_REVIEWED_FILTER_KEY) return reviewers.length === 0;
    const notReviewedEmail = filterKeyToNotReviewedEmail(filterKey);
    if (notReviewedEmail) {
      return !reviewers.includes(notReviewedEmail.trim().toLowerCase());
    }
    if (filterKey === REVIEWED_FILTER_KEY) return reviewers.length > 0;
    if (filterKey === UNCERTAIN_FILTER_KEY) return uncertainBy.length > 0;
    const uncertainEmail = filterKeyToUncertainEmail(filterKey);
    if (uncertainEmail) return uncertainBy.includes(uncertainEmail.trim().toLowerCase());
    if (filterKey === FORLOOP_TEAM_FILTER_KEY) return reviewers.some(isForloopEmail);
    return reviewers.includes(String(filterKey).trim().toLowerCase());
  });
}

/** Search input stays local; parent filters only when user clicks Search or presses Enter. */
function OverviewProductSearchField({ onSearch, isSearching, accentColor }) {
  const [value, setValue] = useState('');
  const submit = () => onSearch(value.trim());
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
      <TextField
        placeholder="Search products..."
        size="small"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: 220 }}
      />
      {isSearching ? (
        <CircularProgress size={22} thickness={4} sx={{ color: accentColor || 'primary.main', flexShrink: 0 }} />
      ) : null}
      <Button
        variant="contained"
        size="small"
        onClick={submit}
        disabled={isSearching}
        sx={{
          textTransform: 'none',
          flexShrink: 0,
          minWidth: 72,
          bgcolor: accentColor,
          '&:hover': { bgcolor: accentColor, filter: 'brightness(0.92)' },
        }}
      >
        Search
      </Button>
    </Box>
  );
}

function JTIProductsOverviewView() {
  const { projectKey } = useParams();
  const { user, isAdmin, uiStyle } = useAuth();
  const styleTokens = useMemo(() => getStyleTokens(projectKey, uiStyle), [projectKey, uiStyle]);
  const viewTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: styleTokens.accent,
            contrastText: '#fff',
          },
        },
      }),
    [styleTokens.accent]
  );
  const userEmail = user?.email?.toLowerCase() || '';
  const {
    getDisplayTitle: getSkuDisplayTitle,
    isTranslationActive: isSkuTranslationActive,
    isTranslatingTitle: isSkuTitleTranslating,
    toggleSkuTitleTranslation,
  } = useSkuTitleTranslations();
  const isEditMode = isAdmin || userEmail === ADMIN_EDIT_EMAIL;
  const canEditUnits = userEmail === ADMIN_EDIT_EMAIL.toLowerCase();
  const canChangeTicketStatus = Boolean(isAdmin);
  const [data, setData] = useState([]);
  const [categoryOverrides, setCategoryOverrides] = useState({});
  const [brandOverrides, setBrandOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(appliedSearchTerm);
  const [isProductSearchPending, startProductSearchTransition] = useTransition();
  const isProductSearchFiltering =
    isProductSearchPending || appliedSearchTerm !== deferredSearchTerm;
  const handleProductSearchSubmit = useCallback((term) => {
    startProductSearchTransition(() => {
      setAppliedSearchTerm(term);
    });
  }, []);
  const [filterBrand, setFilterBrand] = useState('');
  const [filterRecategorized, setFilterRecategorized] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterReviewedBy, setFilterReviewedBy] = useState([]);
  const [uncertainReviewersExpanded, setUncertainReviewersExpanded] = useState(true);
  const [reviewedReviewersExpanded, setReviewedReviewersExpanded] = useState(true);
  const [notReviewedByExpanded, setNotReviewedByExpanded] = useState(true);
  const [columnSettingsAnchor, setColumnSettingsAnchor] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState(loadHiddenColumns);

  const VARIANT_OVERRIDES_KEY = 'jti_variant_overrides';
  const [packSizeOverrides, setPackSizeOverrides] = useState({});
  const [variantOverrides, setVariantOverrides] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(VARIANT_OVERRIDES_KEY) || '{}');
    } catch {
      return {};
    }
  });

  const [matchingGroupsMap, setMatchingGroupsMap] = useState(new Map()); // title -> group name(s)
  const [matchingGroupToVariants, setMatchingGroupToVariants] = useState(new Map()); // group name -> variants[]
  const [matchingGroupToVariantGroups, setMatchingGroupToVariantGroups] = useState(new Map()); // group name -> variantGroups[]
  const [productFlags, setProductFlags] = useState({}); // title -> { isStarterKitOrRegistration, ... }
  const [productReviews, setProductReviews] = useState({}); // title -> string[] (reviewer emails)
  const [bundleContentOverrides, setBundleContentOverrides] = useState({}); // title -> string[]
  const [savingTitle, setSavingTitle] = useState(null);
  const [savingContentTitle, setSavingContentTitle] = useState(null);
  const [savingPortfolioTitle, setSavingPortfolioTitle] = useState(null);
  const [savingPromoOfferTitle, setSavingPromoOfferTitle] = useState(null);
  const [savingForNewUsersTitle, setSavingForNewUsersTitle] = useState(null);
  const [savingSkuVisibilityTitle, setSavingSkuVisibilityTitle] = useState(null);
  const [savingReviewTitle, setSavingReviewTitle] = useState(null);
  const [addContentMenuAnchor, setAddContentMenuAnchor] = useState(null);
  const [addContentRowTitle, setAddContentRowTitle] = useState(null);
  const [addContentOptions, setAddContentOptions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [flagProduct, setFlagProduct] = useState(null);
  const [flagDescription, setFlagDescription] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagSnackbar, setFlagSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [existingFlagTickets, setExistingFlagTickets] = useState([]);
  const [existingFlagTicketsLoading, setExistingFlagTicketsLoading] = useState(false);
  const [flagTicketsByTitle, setFlagTicketsByTitle] = useState({});
  const [expandedFlagTickets, setExpandedFlagTickets] = useState({});
  const [flagTicketDetails, setFlagTicketDetails] = useState({});
  const [savingTicketStatusId, setSavingTicketStatusId] = useState(null);
  const [savingPackSizeTitle, setSavingPackSizeTitle] = useState(null);
  const [productPopupOpen, setProductPopupOpen] = useState(false);
  const [productPopupData, setProductPopupData] = useState({
    productName: null,
    productImage: null,
    productData: null,
    productDataRows: [],
    groupProductNames: undefined,
  });
  const [availableDates, setAvailableDates] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [previewDateRange, setPreviewDateRange] = useState(null);
  const [dateSliderValue, setDateSliderValue] = useState([0, 0]);
  const [titleOverrides, setTitleOverrides] = useState({});
  const [filterDuplicatesOnly, setFilterDuplicatesOnly] = useState(false);
  const [duplicateDialogProduct, setDuplicateDialogProduct] = useState(null);
  const [savingRenameUrl, setSavingRenameUrl] = useState(null);

  const handleSavePackSize = async (title, value) => {
    if (!title || !canEditUnits) return;
    const trimmed = String(value || '').trim();
    setSavingPackSizeTitle(title);
    try {
      if (!trimmed) {
        await axios.delete(`${API_URL}/jti/pack-size-overrides`, { params: { title } });
        setPackSizeOverrides((prev) => {
          const next = { ...prev };
          delete next[title];
          return next;
        });
      } else {
        const n = parseFloat(trimmed);
        if (!isNaN(n) && n > 0) {
          await axios.post(`${API_URL}/jti/pack-size-overrides`, {
            title,
            pack_size: Number.isInteger(n) ? n : n,
          });
          setPackSizeOverrides((prev) => ({
            ...prev,
            [title]: { packSize: n, customPackSize: null },
          }));
        }
      }
    } catch (err) {
      console.error('Error saving pack size:', err);
    } finally {
      setSavingPackSizeTitle(null);
    }
  };

  const handleRenameProductByUrl = async (url, newTitle, originalTitle) => {
    const normalizedUrl = normalizeProductUrl(url);
    const trimmed = String(newTitle || '').trim();
    if (!normalizedUrl || !trimmed) return;
    setSavingRenameUrl(normalizedUrl);
    try {
      await axios.post(`${API_URL}/jti/product-title-overrides`, {
        url: normalizedUrl,
        title: trimmed,
        original_title: originalTitle || '',
      });
      setTitleOverrides((prev) => ({
        ...prev,
        [normalizedUrl]: {
          title: trimmed,
          original_title: originalTitle || getTitleOverrideRecord(normalizedUrl, prev)?.original_title || '',
        },
      }));
      setData((prev) =>
        prev.map((row) =>
          normalizeProductUrl(row.url) === normalizedUrl
            ? { ...row, original_title: row.original_title || originalTitle || row.title, title: trimmed }
            : row
        )
      );
      setFlagSnackbar({ open: true, message: 'Title renamed globally for this URL', severity: 'success' });
    } catch (err) {
      console.error('Error renaming product title:', err);
      setFlagSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to rename title',
        severity: 'error',
      });
    } finally {
      setSavingRenameUrl(null);
    }
  };

  const handleRevertProductTitleByUrl = async (url) => {
    const normalizedUrl = normalizeProductUrl(url);
    if (!normalizedUrl) return;
    const rec = getTitleOverrideRecord(normalizedUrl, titleOverrides);
    const originalTitle = rec?.original_title;
    setSavingRenameUrl(normalizedUrl);
    try {
      await axios.delete(`${API_URL}/jti/product-title-overrides`, { params: { url: normalizedUrl } });
      setTitleOverrides((prev) => {
        const next = { ...prev };
        delete next[normalizedUrl];
        delete next[url];
        return next;
      });
      if (originalTitle) {
        setData((prev) =>
          prev.map((row) =>
            normalizeProductUrl(row.url) === normalizedUrl ? { ...row, title: originalTitle } : row
          )
        );
      }
      setFlagSnackbar({ open: true, message: 'Title override reverted', severity: 'success' });
    } catch (err) {
      console.error('Error reverting product title:', err);
      setFlagSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to revert title',
        severity: 'error',
      });
    } finally {
      setSavingRenameUrl(null);
    }
  };

  const handleSaveBundleContent = async (title, content) => {
    if (!title) return;
    setSavingContentTitle(title);
    try {
      await axios.post(`${API_URL}/jti/bundle-content-overrides`, {
        title,
        content: Array.isArray(content) ? content : [],
      });
      setBundleContentOverrides((prev) => ({ ...prev, [title]: content }));
    } catch (err) {
      console.error('Error saving bundle content:', err);
    } finally {
      setSavingContentTitle(null);
    }
  };

  const handleOpenFlagDialog = (row) => {
    setFlagProduct(row);
    setFlagDescription('');
    setExistingFlagTickets([]);
    setExpandedFlagTickets({});
    setFlagTicketDetails({});
    setSavingTicketStatusId(null);
    setFlagDialogOpen(true);
    if (!row?.title) return;
    setExistingFlagTicketsLoading(true);
    axios
      .get(`${API_URL}/jti/support-tickets`, {
        params: { product_title: row.title },
        withCredentials: true,
      })
      .then(({ data }) => {
        const tickets = Array.isArray(data?.tickets) ? data.tickets : [];
        const details = {};
        tickets.forEach((t) => {
          details[t.id] = {
            loading: false,
            messages: Array.isArray(t.messages) ? t.messages : [],
            message: t.message || '',
            error: null,
          };
        });
        setExistingFlagTickets(tickets);
        setFlagTicketDetails(details);
        setExpandedFlagTickets({});
        if (row.title) {
          setFlagTicketsByTitle((prev) => ({ ...prev, [row.title]: tickets }));
        }
      })
      .catch(() => {
        setExistingFlagTickets([]);
      })
      .finally(() => {
        setExistingFlagTicketsLoading(false);
      });
  };

  const handleCloseFlagDialog = () => {
    setFlagDialogOpen(false);
    setFlagProduct(null);
    setFlagDescription('');
    setExistingFlagTickets([]);
    setExistingFlagTicketsLoading(false);
    setExpandedFlagTickets({});
    setFlagTicketDetails({});
    setSavingTicketStatusId(null);
  };

  const loadFlagTicketDetails = useCallback(async (ticketId, force = false) => {
    let shouldFetch = true;
    setFlagTicketDetails((prev) => {
      const existing = prev[ticketId];
      if (!force && existing && !existing.loading && !existing.error) {
        shouldFetch = false;
        return prev;
      }
      return {
        ...prev,
        [ticketId]: {
          loading: true,
          messages: existing?.messages || [],
          message: existing?.message,
          error: null,
        },
      };
    });
    if (!shouldFetch) return;
    try {
      const { data } = await axios.get(`${API_URL}/support/${ticketId}`, { withCredentials: true });
      setFlagTicketDetails((prev) => ({
        ...prev,
        [ticketId]: {
          loading: false,
          messages: Array.isArray(data?.messages) ? data.messages : [],
          message: data?.message || '',
          error: null,
        },
      }));
      setExistingFlagTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId
            ? { ...t, message: t.message || data?.message || '', status: data?.status || t.status }
            : t
        )
      );
    } catch (err) {
      setFlagTicketDetails((prev) => ({
        ...prev,
        [ticketId]: { loading: false, messages: prev[ticketId]?.messages || [], error: 'Failed to load discussion.' },
      }));
    }
  }, []);

  const toggleFlagTicketDiscussion = (ticketId) => {
    setExpandedFlagTickets((prev) => {
      const current = prev[ticketId] || {};
      return { ...prev, [ticketId]: { ...current, discussion: !current.discussion } };
    });
  };

  const handleFlagTicketStatusChange = async (ticketId, newStatus, productTitle) => {
    const title =
      productTitle ||
      flagProduct?.title ||
      Object.keys(flagTicketsByTitle).find((t) =>
        (flagTicketsByTitle[t] || []).some((ticket) => ticket.id === ticketId)
      );
    const previous =
      (title && (flagTicketsByTitle[title] || []).find((t) => t.id === ticketId)?.status) ||
      existingFlagTickets.find((t) => t.id === ticketId)?.status ||
      'Open';
    setSavingTicketStatusId(ticketId);
    setExistingFlagTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, status: newStatus } : t))
    );
    if (title) {
      setFlagTicketsByTitle((prev) => ({
        ...prev,
        [title]: (prev[title] || []).map((t) => (t.id === ticketId ? { ...t, status: newStatus } : t)),
      }));
    }
    try {
      await axios.put(`${API_URL}/support/${ticketId}`, { status: newStatus }, { withCredentials: true });
      loadFlagTicketDetails(ticketId, true);
    } catch (err) {
      setExistingFlagTickets((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, status: previous } : t))
      );
      if (title) {
        setFlagTicketsByTitle((prev) => ({
          ...prev,
          [title]: (prev[title] || []).map((t) => (t.id === ticketId ? { ...t, status: previous } : t)),
        }));
      }
      setFlagSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to update ticket status.',
        severity: 'error',
      });
    } finally {
      setSavingTicketStatusId(null);
    }
  };

  const handleToggleReview = async (title) => {
    if (!title || !userEmail) return;
    setSavingReviewTitle(title);
    try {
      const { data } = await axios.post(`${API_URL}/jti/product-reviews`, {
        title,
        user_email: userEmail,
      });
      setProductReviews((prev) => ({ ...prev, [title]: data.reviewers || [] }));

      const wasAdded = (data.reviewers || []).some((e) => e.toLowerCase() === userEmail.toLowerCase());
      if (wasAdded) {
        const uncertainBy = getUncertainBy(productFlags, title);
        if (uncertainBy.some((e) => e.toLowerCase() === userEmail.toLowerCase())) {
          const newList = uncertainBy.filter((e) => e.toLowerCase() !== userEmail.toLowerCase());
          if (newList.length === 0) {
            await axios.delete(`${API_URL}/jti/product-flags`, { params: { title, flag: 'uncertainBy' } });
            setProductFlags((prev) => {
              const next = { ...prev };
              if (next[title]) {
                const { uncertainBy: _, ...rest } = next[title];
                if (Object.keys(rest).length) next[title] = rest;
                else delete next[title];
              }
              return next;
            });
          } else {
            await axios.post(`${API_URL}/jti/product-flags`, { title, flag: 'uncertainBy', value: newList });
            setProductFlags((prev) => ({ ...prev, [title]: { ...(prev[title] || {}), uncertainBy: newList } }));
          }
        }
      }
    } catch (err) {
      console.error('Error toggling review:', err);
    } finally {
      setSavingReviewTitle(null);
    }
  };

  const handleToggleUncertain = async (title) => {
    if (!title || !userEmail) return;
    setSavingReviewTitle(title);
    try {
      const uncertainBy = getUncertainBy(productFlags, title);
      const isUncertainByMe = uncertainBy.some((e) => e.toLowerCase() === userEmail.toLowerCase());

      if (isUncertainByMe) {
        const newList = uncertainBy.filter((e) => e.toLowerCase() !== userEmail.toLowerCase());
        if (newList.length === 0) {
          await axios.delete(`${API_URL}/jti/product-flags`, { params: { title, flag: 'uncertainBy' } });
          setProductFlags((prev) => {
            const next = { ...prev };
            if (next[title]) {
              const { uncertainBy: _, ...rest } = next[title];
              if (Object.keys(rest).length) next[title] = rest;
              else delete next[title];
            }
            return next;
          });
        } else {
          await axios.post(`${API_URL}/jti/product-flags`, { title, flag: 'uncertainBy', value: newList });
          setProductFlags((prev) => ({ ...prev, [title]: { ...(prev[title] || {}), uncertainBy: newList } }));
        }
        return;
      }

      const reviewers = productReviews[title] || [];
      if (reviewers.some((e) => e.toLowerCase() === userEmail.toLowerCase())) {
        const { data } = await axios.post(`${API_URL}/jti/product-reviews`, {
          title,
          user_email: userEmail,
        });
        setProductReviews((prev) => ({ ...prev, [title]: data.reviewers || [] }));
      }

      const newList = [...uncertainBy.filter((e) => e.toLowerCase() !== userEmail.toLowerCase()), userEmail];
      await axios.post(`${API_URL}/jti/product-flags`, { title, flag: 'uncertainBy', value: newList });
      setProductFlags((prev) => ({ ...prev, [title]: { ...(prev[title] || {}), uncertainBy: newList } }));
    } catch (err) {
      console.error('Error toggling uncertain:', err);
    } finally {
      setSavingReviewTitle(null);
    }
  };

  const handleSubmitFlag = async () => {
    if (!flagProduct?.title) return;
    setFlagSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/jti/support-ticket`,
        {
          product_title: flagProduct.title,
          description: flagDescription.trim() || undefined,
          user_email: user?.email || undefined,
        },
        { withCredentials: true }
      );
      const title = flagProduct.title;
      setFlagTicketsByTitle((prev) => {
        const nextTicket = {
          id: data?.ticket_id,
          status: 'Open',
          is_mine: true,
          product_title: title,
          message: flagDescription.trim() || '',
        };
        return { ...prev, [title]: [nextTicket, ...(prev[title] || [])] };
      });
      if (userEmail) {
        setProductFlags((prev) => {
          const reported = getIncorrectInfoReportedBy(prev, title);
          if (reported.some((e) => e.toLowerCase() === userEmail)) {
            return prev;
          }
          return {
            ...prev,
            [title]: { ...(prev[title] || {}), incorrectInfoReportedBy: [...reported, userEmail] },
          };
        });
      }
      setFlagSnackbar({ open: true, message: 'Support ticket created successfully.', severity: 'success' });
      handleCloseFlagDialog();
    } catch (err) {
      setFlagSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to create support ticket.',
        severity: 'error',
      });
    } finally {
      setFlagSubmitting(false);
    }
  };

  const handleRecategorize = async (title, categoryGroup) => {
    const normalizedTitle = String(title || '').trim();
    if (!normalizedTitle) return;
    setSavingTitle(title);
    try {
      if (categoryGroup) {
        await axios.post(`${API_URL}/jti/product-category-overrides`, {
          title: normalizedTitle,
          category_group: categoryGroup,
        });
        setCategoryOverrides((prev) => ({
          ...prev,
          [normalizedTitle]: categoryGroup,
          ...(title && title !== normalizedTitle ? { [title]: categoryGroup } : {}),
        }));
      } else {
        await axios.delete(`${API_URL}/jti/product-category-overrides`, {
          params: { title: normalizedTitle },
        });
        setCategoryOverrides((prev) => {
          const next = { ...prev };
          delete next[normalizedTitle];
          if (title) delete next[title];
          return next;
        });
      }
    } catch (err) {
      console.error('Error saving recategorization:', err);
    } finally {
      setSavingTitle(null);
    }
  };

  const handleSetPortfolioType = async (title, value) => {
    if (!title) return;
    setSavingPortfolioTitle(title);
    try {
      if (value) {
        await axios.post(`${API_URL}/jti/product-flags`, {
          title,
          flag: 'portfolioType',
          value,
        });
        setProductFlags((prev) => ({ ...prev, [title]: { ...(prev[title] || {}), portfolioType: value } }));
      } else {
        await axios.delete(`${API_URL}/jti/product-flags`, { params: { title, flag: 'portfolioType' } });
        setProductFlags((prev) => {
          const next = { ...prev };
          if (next[title]) {
            const { portfolioType: _, ...rest } = next[title];
            if (Object.keys(rest).length) next[title] = rest;
            else delete next[title];
          }
          return next;
        });
      }
    } catch (err) {
      console.error('Error saving portfolio type:', err);
    } finally {
      setSavingPortfolioTitle(null);
    }
  };

  const handleSetPromoOffer = async (title, isPromo) => {
    if (!title) return;
    setSavingPromoOfferTitle(title);
    try {
      if (isPromo) {
        await axios.post(`${API_URL}/jti/product-flags`, {
          title,
          flag: 'isPromoOffer',
          value: true,
        });
        setProductFlags((prev) => ({ ...prev, [title]: { ...(prev[title] || {}), isPromoOffer: true } }));
      } else {
        await axios.delete(`${API_URL}/jti/product-flags`, { params: { title, flag: 'isPromoOffer' } });
        setProductFlags((prev) => {
          const next = { ...prev };
          if (next[title]) {
            const { isPromoOffer: _, ...rest } = next[title];
            if (Object.keys(rest).length) next[title] = rest;
            else delete next[title];
          }
          return next;
        });
      }
    } catch (err) {
      console.error('Error saving promo offer flag:', err);
    } finally {
      setSavingPromoOfferTitle(null);
    }
  };

  const handleSetForNewUsersOnly = async (title, enabled) => {
    if (!title) return;
    setSavingForNewUsersTitle(title);
    try {
      if (enabled) {
        await axios.post(`${API_URL}/jti/product-flags`, {
          title,
          flag: 'isForNewUsersOnly',
          value: true,
        });
        setProductFlags((prev) => ({ ...prev, [title]: { ...(prev[title] || {}), isForNewUsersOnly: true } }));
      } else {
        await axios.delete(`${API_URL}/jti/product-flags`, { params: { title, flag: 'isForNewUsersOnly' } });
        setProductFlags((prev) => {
          const next = { ...prev };
          if (next[title]) {
            const { isForNewUsersOnly: _, ...rest } = next[title];
            if (Object.keys(rest).length) next[title] = rest;
            else delete next[title];
          }
          return next;
        });
      }
    } catch (err) {
      console.error('Error saving for-new-users-only flag:', err);
    } finally {
      setSavingForNewUsersTitle(null);
    }
  };

  const handleSetSkuVisibility = async (title, value) => {
    if (!title) return;
    const newVal = normalizeSkuVisibility(value);
    setSavingSkuVisibilityTitle(title);
    try {
      await axios.post(`${API_URL}/jti/product-flags`, {
        title,
        flag: PRODUCT_FLAG_SKU_VISIBILITY,
        value: newVal,
      });
      setProductFlags((prev) => ({
        ...prev,
        [title]: { ...(prev[title] || {}), [PRODUCT_FLAG_SKU_VISIBILITY]: newVal },
      }));
    } catch (err) {
      console.error('Error saving SKU visibility:', err);
    } finally {
      setSavingSkuVisibilityTitle(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dataResponse, overridesResponse, brandOverridesResponse, matchingResponse, flagsResponse, bundleContentResponse, packSizeResponse, reviewsResponse, flagTicketsResponse, titleOverridesResponse] = await Promise.all([
          axios.get(`${API_URL}/jti/global-merged-data`),
          axios.get(`${API_URL}/jti/product-category-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/product-brand-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/matching-groups`).catch(() => ({ data: { tables: {} } })),
          axios.get(`${API_URL}/jti/product-flags`).catch(() => ({ data: { flags: {} } })),
          axios.get(`${API_URL}/jti/bundle-content-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/pack-size-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/product-reviews`).catch(() => ({ data: { reviews: {} } })),
          axios.get(`${API_URL}/jti/support-tickets`, { withCredentials: true }).catch(() => ({ data: { tickets: [] } })),
          axios.get(`${API_URL}/jti/product-title-overrides`).catch(() => ({ data: { overrides: {} } })),
        ]);

        const { columns: apiColumns, data: apiData } = dataResponse.data;
        const loadedTitleOverrides = titleOverridesResponse?.data?.overrides || {};
        setTitleOverrides(loadedTitleOverrides);

        const processedData = applyTitleOverridesToRows(
          apiData.map((row) => {
            const obj = {};
            apiColumns.forEach((col, index) => {
              obj[col] = row[index];
            });
            const rawDate = obj.updated_at || obj.date;
            if (rawDate) {
              const parsed = dayjs(rawDate);
              obj.__ts = parsed.isValid() ? parsed.valueOf() : null;
            } else {
              obj.__ts = null;
            }
            return obj;
          }),
          loadedTitleOverrides
        );

        setData(processedData);
        setCategoryOverrides(indexOverridesByTitle(overridesResponse?.data?.overrides || {}));
        setBrandOverrides(brandOverridesResponse?.data?.overrides || {});

        const rawDates = processedData
          .map((item) => item.updated_at || item.date)
          .filter((date) => date != null && date !== '')
          .map((date) => dayjs(date))
          .filter((date) => date.isValid())
          .map((date) => date.startOf('day'))
          .sort((a, b) => a.valueOf() - b.valueOf());
        const dates = [];
        const seenDates = new Set();
        rawDates.forEach((date) => {
          const key = date.format('YYYY-MM-DD');
          if (!seenDates.has(key)) {
            seenDates.add(key);
            dates.push(date);
          }
        });
        if (dates.length > 0) {
          const latest = dates[dates.length - 1];
          setAvailableDates(dates);
          setDateRange([latest, latest]);
        } else {
          setAvailableDates([]);
          setDateRange([null, null]);
        }

        const tables = matchingResponse?.data?.tables || {};
        const skuToGroups = new Map();
        const groupToVariants = new Map();
        const groupToVariantGroups = new Map(); // groupName -> { name -> Set(variants) } merged
        const mergeVariantGroupsInto = (targetMap, vgs) => {
          if (!vgs?.length) return;
          vgs.forEach((vg) => {
            const vgName = (vg.name || '').trim() || '(unnamed)';
            if (!targetMap.has(vgName)) targetMap.set(vgName, new Set());
            (vg.variants || []).forEach((v) => {
              const t = sanitizeVariant(v);
              if (t) targetMap.get(vgName).add(t);
            });
          });
        };
        Object.values(tables).forEach((table) => {
          (table?.groups || []).forEach((group) => {
            const name = group?.name;
            const skus = group?.skus || [];
            const variants = getFlatVariantsFromGroup(group);
            const vgs = group?.variantGroups?.length ? group.variantGroups : (group?.variants?.length ? [{ name: '', variants: group.variants }] : []);
            if (name && Array.isArray(skus)) {
              skus.forEach((sku) => {
                if (sku) {
                  if (!skuToGroups.has(sku)) skuToGroups.set(sku, new Set());
                  skuToGroups.get(sku).add(name);
                }
              });
              if (variants.length) {
                if (!groupToVariants.has(name)) groupToVariants.set(name, new Set());
                variants.forEach((v) => v && groupToVariants.get(name).add(String(v).trim()));
              }
              if (vgs.length) {
                if (!groupToVariantGroups.has(name)) groupToVariantGroups.set(name, new Map());
                mergeVariantGroupsInto(groupToVariantGroups.get(name), vgs);
              }
            }
          });
        });
        setMatchingGroupsMap(skuToGroups);
        setMatchingGroupToVariants(groupToVariants);
        setMatchingGroupToVariantGroups(groupToVariantGroups);
        setProductFlags(flagsResponse?.data?.flags || {});
        setProductReviews(reviewsResponse?.data?.reviews || {});
        setFlagTicketsByTitle(groupFlagTicketsByTitle(flagTicketsResponse?.data?.tickets || []));
        setBundleContentOverrides(bundleContentResponse?.data?.overrides || {});
        setPackSizeOverrides(packSizeResponse?.data?.overrides || {});
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load JTI products data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getEffectiveCategoryGroup = (title, originalCategory, originalTitle) => {
    return getOverrideValue(categoryOverrides, title, originalTitle) || originalCategory || '';
  };

  const getEffectiveBrand = (title, datasetBrand) =>
    getEffectiveBrandFromOverrides(title, datasetBrand, brandOverrides);

  const getEffectivePackSize = (title, row) => {
    const saved = packSizeOverrides[title];
    if (saved) {
      if (saved.packSize === 'other') {
        const n = Number(saved.customPackSize);
        return !isNaN(n) && n > 0 ? n : null;
      }
      const n = Number(saved.packSize);
      return !isNaN(n) && n > 0 ? n : null;
    }
    const raw = row?.['Units (#)'] ?? row?.units;
    const n = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : NaN;
    return !isNaN(n) && n > 0 ? n : null;
  };

  const effectiveDateRange = previewDateRange || dateRange;

  const productSeenDatesByKey = useMemo(() => {
    const map = new Map();
    for (const row of data) {
      const title = row.title;
      if (!title) continue;
      const country = (row.country || '').toString().trim() || 'Unknown';
      const key = overviewProductKey(country, title);
      const ts = row.__ts;
      if (ts == null) continue;
      const prev = map.get(key);
      if (!prev) {
        map.set(key, { firstSeenTs: ts, lastSeenTs: ts });
      } else {
        if (ts < prev.firstSeenTs) prev.firstSeenTs = ts;
        if (ts > prev.lastSeenTs) prev.lastSeenTs = ts;
      }
    }
    return map;
  }, [data]);

  const dateFilteredData = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return data;
    const dateLowerBound = dateRange[0].subtract(1, 'day').valueOf();
    const dateUpperBound = dateRange[1].add(1, 'day').valueOf();
    return data.filter((item) => {
      let ts = item.__ts;
      if (ts === undefined) {
        const parsed = dayjs(item.updated_at || item.date);
        ts = parsed.isValid() ? parsed.valueOf() : null;
      }
      return ts != null && ts > dateLowerBound && ts < dateUpperBound;
    });
  }, [data, dateRange]);

  const findClosestDateIndex = (targetDate) => {
    if (!availableDates.length || !targetDate?.isValid?.()) return -1;
    let closestIdx = -1;
    let smallestDiff = Infinity;
    availableDates.forEach((date, idx) => {
      const diff = Math.abs(date.diff(targetDate, 'millisecond'));
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestIdx = idx;
      }
    });
    return closestIdx;
  };

  useEffect(() => {
    if (!availableDates.length) return;
    const firstDate = availableDates[0];
    const lastDate = availableDates[availableDates.length - 1];
    const startDate = dateRange[0] && dateRange[0].isValid() ? dateRange[0] : firstDate;
    const endDate = dateRange[1] && dateRange[1].isValid() ? dateRange[1] : lastDate;
    const clampedStart = startDate.isBefore(firstDate, 'day') ? firstDate : startDate;
    const clampedEnd = endDate.isAfter(lastDate, 'day') ? lastDate : endDate;
    let startIdx = availableDates.findIndex((d) => d.isSame(clampedStart, 'day'));
    let endIdx = availableDates.findIndex((d) => d.isSame(clampedEnd, 'day'));
    if (startIdx === -1) startIdx = findClosestDateIndex(clampedStart);
    if (endIdx === -1) endIdx = findClosestDateIndex(clampedEnd);
    const safeStart = startIdx === -1 ? 0 : startIdx;
    const safeEnd = endIdx === -1 ? availableDates.length - 1 : endIdx;
    const nextStart = availableDates[safeStart] || firstDate;
    const nextEnd = availableDates[safeEnd] || lastDate;
    if (
      !dateRange[0] ||
      !dateRange[1] ||
      !dateRange[0].isValid() ||
      !dateRange[1].isValid() ||
      !dateRange[0].isSame(nextStart, 'day') ||
      !dateRange[1].isSame(nextEnd, 'day')
    ) {
      setDateRange([nextStart, nextEnd]);
      return;
    }
    setDateSliderValue([Math.min(safeStart, safeEnd), Math.max(safeStart, safeEnd)]);
  }, [availableDates, dateRange]);

  const handleDateSliderChange = (_, newValue) => {
    const nextValue = Array.isArray(newValue) ? newValue : [newValue, newValue];
    const [rawStart, rawEnd] = nextValue;
    const startIdx = Math.min(rawStart, rawEnd);
    const endIdx = Math.max(rawStart, rawEnd);
    setDateSliderValue([startIdx, endIdx]);
    if (!availableDates.length) return;
    const nextStart = availableDates[startIdx];
    const nextEnd = availableDates[endIdx];
    if (!nextStart || !nextEnd) return;
    setPreviewDateRange([nextStart, nextEnd]);
  };

  const handleDateSliderCommit = (_, newValue) => {
    setPreviewDateRange(null);
    if (!availableDates.length) return;
    const [rawStart, rawEnd] = Array.isArray(newValue) ? newValue : [newValue, newValue];
    const startIdx = Math.min(rawStart, rawEnd);
    const endIdx = Math.max(rawStart, rawEnd);
    setDateRange([availableDates[startIdx], availableDates[endIdx]]);
  };

  const getInferredVariants = (title) => {
    const seen = new Set();
    dateFilteredData
      .filter((item) => item.title === title)
      .forEach((item) => {
        [item.variant, item.product_variants, item.product_variant_names].forEach((raw) => {
          if (!raw) return;
          let values = [];
          if (Array.isArray(raw)) {
            values = raw.map(String);
          } else {
            const str = String(raw).trim();
            if (str.startsWith('[')) {
              try {
                values = JSON.parse(str).map(String);
              } catch {
                values = [str];
              }
            } else {
              values = str.split(/\s*[,|;]\s*/).filter(Boolean);
            }
          }
          values.forEach((v) => {
            const trimmed = sanitizeVariant(v);
            if (trimmed && !['n/a', 'none', 'null'].includes(trimmed.toLowerCase())) {
              seen.add(trimmed);
            }
          });
        });
      });
    return [...seen].sort();
  };

  const matchInferredToGroupVariants = (inferred, groupVariants) => {
    if (!groupVariants?.length) return [];
    const matched = new Set();
    inferred.forEach((v) => {
      const vl = v.toLowerCase();
      const found = groupVariants.find((gv) => {
        const gvl = gv.toLowerCase();
        return vl === gvl || vl.includes(gvl) || gvl.includes(vl);
      });
      if (found) matched.add(found);
    });
    return [...matched].sort();
  };

  const getEffectiveVariants = (title) => {
    const saved = variantOverrides[title];
    if (saved !== undefined) return saved;
    const inferred = getInferredVariants(title);
    const groupNames = matchingGroupsMap.get(title);
    const groupVariants = groupNames
      ? [...groupNames].flatMap((gn) => [...(matchingGroupToVariants.get(gn) || [])])
      : [];
    if (groupVariants.length > 0) {
      return matchInferredToGroupVariants(inferred, groupVariants);
    }
    return inferred;
  };

  // Unique groups from matching for Devices and Sticks (for bundle content options)
  const { deviceGroupsOptions, sticksGroupsOptions } = useMemo(() => {
    const seenByTitle = new Set();
    const deviceGroups = new Set();
    const sticksGroups = new Set();
    for (const row of dateFilteredData) {
      const title = row.title;
      if (!title || seenByTitle.has(title)) continue;
      seenByTitle.add(title);
      const effectiveCategory = getEffectiveCategoryGroup(title, row.category_group, row.original_title);
      const groupNames = matchingGroupsMap.get(title);
      const labels = groupNames?.size ? [...groupNames] : [title];
      if (effectiveCategory === 'Devices') {
        labels.forEach((l) => deviceGroups.add(l));
      } else if (effectiveCategory === 'Sticks') {
        labels.forEach((l) => sticksGroups.add(l));
      }
    }
    return {
      deviceGroupsOptions: [...deviceGroups].sort(),
      sticksGroupsOptions: [...sticksGroups].sort(),
    };
  }, [dateFilteredData, categoryOverrides, matchingGroupsMap]);

  const deviceBundlesContentOptions = useMemo(
    () => [...new Set([...deviceGroupsOptions, ...sticksGroupsOptions])].sort(),
    [deviceGroupsOptions, sticksGroupsOptions]
  );

  // Derive unique products: one row per country + product title
  const uniqueProducts = useMemo(() => {
    const byKey = new Map();
    for (const row of dateFilteredData) {
      const title = row.title;
      if (!title) continue;
      const country = (row.country || '').toString().trim() || 'Unknown';
      const key = overviewProductKey(country, title);
      if (!byKey.has(key)) {
        const seen = productSeenDatesByKey.get(key);
        byKey.set(key, {
          id: key,
          title,
          country,
          brand: getEffectiveBrand(title, row.brand || ''),
          original_title: row.original_title || title,
          original_category_group: row.category_group || '',
          first_seen_date: formatSeenDate(seen?.firstSeenTs),
          last_seen_date: formatSeenDate(seen?.lastSeenTs),
          retailer_count: 0,
          units: new Set(),
          contentParts: new Set(),
          _urls: isEditMode ? new Map() : null,
          _urlsByDate: isEditMode ? new Map() : null,
        });
      }
      const entry = byKey.get(key);
      entry.retailer_count += 1;
      if (isEditMode && entry._urls) {
        const listingUrl = normalizeProductUrl(row.url);
        if (listingUrl) {
          if (!entry._urls.has(listingUrl)) {
            entry._urls.set(listingUrl, {
              url: listingUrl,
              displayUrl: String(row.url || listingUrl).trim() || listingUrl,
              count: 0,
              retailers: new Set(),
              dates: new Set(),
            });
          }
          const urlInfo = entry._urls.get(listingUrl);
          urlInfo.count += 1;
          if (row.retailer) urlInfo.retailers.add(String(row.retailer).trim());
          const dateKey = dateKeyFromOverviewRow(row);
          if (dateKey) {
            urlInfo.dates.add(dateKey);
            if (!entry._urlsByDate.has(dateKey)) entry._urlsByDate.set(dateKey, new Set());
            entry._urlsByDate.get(dateKey).add(listingUrl);
          }
        }
      }
      const packSize = getEffectivePackSize(title, row);
      if (packSize != null) entry.units.add(packSize);
      [row.flavor_intensity, row.intensity, row.cooling].forEach((v) => {
        if (v && String(v).trim()) entry.contentParts.add(String(v).trim());
      });
    }
    return Array.from(byKey.values()).map((p) => {
      const effectiveCategory = getEffectiveCategoryGroup(p.title, p.original_category_group, p.original_title);
      const unitsArr = [...p.units].sort((a, b) => a - b);
      const variants = getEffectiveVariants(p.title);
      const groupNames = matchingGroupsMap.get(p.title);
      const matching_group = groupNames?.size ? [...groupNames].sort().join(', ') : null;
      const variantGroups = [];
      if (groupNames?.size) {
        const merged = new Map();
        groupNames.forEach((gn) => {
          const vgMap = matchingGroupToVariantGroups.get(gn);
          if (vgMap) {
            vgMap.forEach((vs, vgName) => {
              if (!merged.has(vgName)) merged.set(vgName, new Set());
              vs.forEach((v) => merged.get(vgName).add(v));
            });
          }
        });
        merged.forEach((vs, vgName) => {
          const list = [...vs].sort();
          if (list.length) variantGroups.push({ name: vgName === '(unnamed)' ? 'Variants' : vgName, variants: list });
        });
        variantGroups.sort((a, b) => a.name.localeCompare(b.name));
      }
      const variantGroupsDisplay =
        variantGroups.length > 0
          ? variantGroups.map((vg) => `${vg.name}: ${vg.variants.join(', ')}`).join(' | ')
          : variants.length > 0
            ? variants.join(', ')
            : null;
      const flagStarterKit = productFlags[p.title]?.isStarterKitOrRegistration === true;
      const is_starter_kit = effectiveCategory === 'Starter Kits' || flagStarterKit;
      const portfolioType = productFlags[p.title]?.portfolioType || null;
      const is_promo_offer = productFlags[p.title]?.isPromoOffer === true;
      const is_for_new_users_only = productFlags[p.title]?.isForNewUsersOnly === true;
      const sku_visibility = getSkuVisibility(productFlags, p.title);
      const isDeviceOrSticksBundle = effectiveCategory === 'Device Bundles' || effectiveCategory === 'Sticks Bundles';
      const bundleContent = bundleContentOverrides[p.title];
      const content = isDeviceOrSticksBundle && bundleContent?.length
        ? formatBundleContentWithCounts(bundleContent)
        : ([...p.contentParts].filter(Boolean).join(' | ') || null);
      const brandStr = p.brand || '';
      const titleStr = p.title || '';
      const _reviewersLower = (productReviews[p.title] || [])
        .map((e) => String(e).trim().toLowerCase())
        .filter(Boolean);
      const _uncertainByLower = getUncertainBy(productFlags, p.title)
        .map((e) => String(e).trim().toLowerCase())
        .filter(Boolean);
      const {
        _urls,
        _urlsByDate,
        units: _units,
        contentParts: _contentParts,
        ...productBase
      } = p;
      const listingUrls = _urls
        ? [..._urls.values()]
            .map((info) => ({
              url: info.url,
              displayUrl: info.displayUrl,
              count: info.count,
              retailers: [...info.retailers].filter(Boolean).sort(),
              dates: [...info.dates].sort(),
            }))
            .sort((a, b) => a.url.localeCompare(b.url))
        : [];
      const duplicateDates = [];
      if (_urlsByDate) {
        _urlsByDate.forEach((urlSet, date) => {
          if (urlSet.size > 1) {
            duplicateDates.push({ date, urls: [...urlSet].sort() });
          }
        });
        duplicateDates.sort((a, b) => a.date.localeCompare(b.date));
      }
      const hasDuplicateSkuTitle = duplicateDates.length > 0;
      const titleOverridden = listingUrls.some((u) => Boolean(getTitleOverrideRecord(u.url, titleOverrides)));
      return {
        ...productBase,
        countries: p.country,
        country_count: 1,
        recategorized_category_group: effectiveCategory,
        _searchHaystack: `${titleStr}\n${brandStr}`.toLowerCase(),
        _countryKey: normalizeOverviewCountryKey(p.country),
        _reviewersLower,
        _uncertainByLower,
        is_starter_kit,
        portfolioType,
        is_promo_offer,
        is_for_new_users_only,
        sku_visibility,
        isDeviceOrSticksBundle,
        number_of_units: unitsArr.length ? unitsArr.join(', ') : null,
        variants: variantGroupsDisplay,
        content,
        bundleContentList: isDeviceOrSticksBundle ? (bundleContentOverrides[p.title] || []) : null,
        matching_group,
        listingUrls,
        duplicateDates,
        hasDuplicateSkuTitle,
        titleOverridden,
      };
    });
  }, [dateFilteredData, categoryOverrides, brandOverrides, packSizeOverrides, variantOverrides, matchingGroupsMap, matchingGroupToVariants, matchingGroupToVariantGroups, productFlags, bundleContentOverrides, productReviews, productSeenDatesByKey, titleOverrides, isEditMode]);

  const filteredProducts = useMemo(() => {
    const term = deferredSearchTerm ? deferredSearchTerm.toLowerCase() : '';
    const countryKey = filterCountry ? normalizeOverviewCountryKey(filterCountry) : '';
    const out = [];
    for (const p of uniqueProducts) {
      if (term && !p._searchHaystack.includes(term)) continue;
      if (filterBrand && p.brand !== filterBrand) continue;
      if (filterRecategorized && p.recategorized_category_group !== filterRecategorized) continue;
      if (countryKey && p._countryKey !== countryKey) continue;
      if (filterReviewedBy.length > 0 && !productMatchesReviewFilters(p, filterReviewedBy)) continue;
      if (isEditMode && filterDuplicatesOnly && !p.hasDuplicateSkuTitle) continue;
      out.push(p);
    }
    return out;
  }, [
    uniqueProducts,
    deferredSearchTerm,
    filterBrand,
    filterRecategorized,
    filterCountry,
    filterReviewedBy,
    isEditMode,
    filterDuplicatesOnly,
  ]);

  const duplicateSkuCount = useMemo(
    () => uniqueProducts.reduce((n, p) => n + (p.hasDuplicateSkuTitle ? 1 : 0), 0),
    [uniqueProducts]
  );

  useEffect(() => {
    if (!duplicateDialogProduct) return;
    const next = uniqueProducts.find((p) => p.id === duplicateDialogProduct.id);
    if (!next) {
      setDuplicateDialogProduct(null);
      return;
    }
    if (next !== duplicateDialogProduct) setDuplicateDialogProduct(next);
  }, [uniqueProducts, duplicateDialogProduct]);

  const paginatedProducts = useMemo(
    () => filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredProducts, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [deferredSearchTerm, filterBrand, filterRecategorized, filterCountry, filterReviewedBy, filterDuplicatesOnly, dateRange]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredProducts.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredProducts.length, rowsPerPage, page]);

  const expandDateRangeByDays = useCallback((days) => {
    if (!dateRange[0]?.isValid?.() || !dateRange[1]?.isValid?.()) return;
    if (!availableDates.length) return;
    setPreviewDateRange(null);
    const firstDate = availableDates[0];
    const lastDate = availableDates[availableDates.length - 1];
    // Look further back by N days while keeping the current end date.
    let nextStart = dateRange[0].subtract(days, 'day');
    let nextEnd = dateRange[1];
    if (nextStart.isBefore(firstDate, 'day')) nextStart = firstDate;
    if (nextEnd.isAfter(lastDate, 'day')) nextEnd = lastDate;
    if (nextStart.isAfter(nextEnd, 'day')) nextStart = nextEnd;

    const closestIdx = (target) => {
      let closest = 0;
      let smallestDiff = Infinity;
      availableDates.forEach((date, idx) => {
        const diff = Math.abs(date.diff(target, 'millisecond'));
        if (diff < smallestDiff) {
          smallestDiff = diff;
          closest = idx;
        }
      });
      return closest;
    };

    let startIdx = availableDates.findIndex((d) => d.isSame(nextStart, 'day'));
    let endIdx = availableDates.findIndex((d) => d.isSame(nextEnd, 'day'));
    if (startIdx === -1) startIdx = closestIdx(nextStart);
    if (endIdx === -1) endIdx = closestIdx(nextEnd);
    if (startIdx > endIdx) [startIdx, endIdx] = [endIdx, startIdx];
    setDateRange([availableDates[startIdx], availableDates[endIdx]]);
  }, [availableDates, dateRange]);

  const showEmptyDateRangeSuggestion =
    !loading &&
    filteredProducts.length === 0 &&
    Boolean(dateRange[0]?.isValid?.() && dateRange[1]?.isValid?.()) &&
    data.length > 0;

  const brands = useMemo(() => {
    let products = uniqueProducts;
    if (filterRecategorized) {
      products = products.filter((p) => p.recategorized_category_group === filterRecategorized);
    }
    return [...new Set(products.map((p) => p.brand).filter(Boolean))].sort();
  }, [uniqueProducts, filterRecategorized, categoryOverrides]);

  useEffect(() => {
    if (filterBrand && !brands.includes(filterBrand)) {
      setFilterBrand('');
    }
  }, [brands, filterBrand]);

  const countries = useMemo(() => {
    return [...new Set(uniqueProducts.map((p) => p.country).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [uniqueProducts]);

  const reviewerEmails = useMemo(() => {
    const allEmails = new Set();
    Object.values(productReviews).forEach((reviewers) => {
      (reviewers || []).forEach((email) => {
        const trimmed = String(email || '').trim();
        if (trimmed) allEmails.add(trimmed);
      });
    });
    return [...allEmails].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [productReviews]);

  const { forloopReviewerEmails, otherReviewerEmails } = useMemo(() => ({
    forloopReviewerEmails: reviewerEmails.filter(isForloopEmail),
    otherReviewerEmails: reviewerEmails.filter((email) => !isForloopEmail(email)),
  }), [reviewerEmails]);

  const notReviewedByEmails = useMemo(() => {
    const byLower = new Map();
    reviewerEmails.forEach((email) => {
      const trimmed = String(email || '').trim();
      if (trimmed) byLower.set(trimmed.toLowerCase(), trimmed);
    });
    const me = String(user?.email || '').trim();
    if (me && !byLower.has(me.toLowerCase())) {
      byLower.set(me.toLowerCase(), me);
    }
    return [...byLower.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [reviewerEmails, user?.email]);

  const uncertainFlaggerEmails = useMemo(() => {
    const allEmails = new Set();
    Object.values(productFlags).forEach((flags) => {
      (flags?.uncertainBy || []).forEach((email) => {
        const trimmed = String(email || '').trim();
        if (trimmed) allEmails.add(trimmed);
      });
    });
    return [...allEmails].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [productFlags]);

  const formatReviewedFilterLabel = (value) => {
    if (value === NOT_REVIEWED_FILTER_KEY) return 'Not reviewed by';
    const notReviewedEmail = filterKeyToNotReviewedEmail(value);
    if (notReviewedEmail) return `Not reviewed by ${notReviewedEmail}`;
    if (value === REVIEWED_FILTER_KEY) return 'Reviewed';
    if (value === UNCERTAIN_FILTER_KEY) return 'Uncertain';
    if (value === FORLOOP_TEAM_FILTER_KEY) return 'Forloop team';
    const uncertainEmail = filterKeyToUncertainEmail(value);
    if (uncertainEmail) return uncertainEmail;
    return value;
  };

  const isSpecialReviewFilterKey = (key) =>
    key === NOT_REVIEWED_FILTER_KEY ||
    key === REVIEWED_FILTER_KEY ||
    key === FORLOOP_TEAM_FILTER_KEY ||
    key === UNCERTAIN_FILTER_KEY ||
    String(key).startsWith(UNCERTAIN_EMAIL_FILTER_PREFIX) ||
    String(key).startsWith(NOT_REVIEWED_BY_EMAIL_FILTER_PREFIX);

  const isReviewedFilterSelected = (key) => {
    if (isSpecialReviewFilterKey(key)) {
      return filterReviewedBy.includes(key);
    }
    const normalized = String(key).trim().toLowerCase();
    return filterReviewedBy.some((v) => String(v).trim().toLowerCase() === normalized);
  };

  const toggleReviewedFilter = (key) => {
    setFilterReviewedBy((prev) => {
      if (isSpecialReviewFilterKey(key)) {
        return prev.includes(key) ? prev.filter((v) => v !== key) : [...prev, key];
      }
      const normalized = String(key).trim().toLowerCase();
      const existing = prev.find((v) => String(v).trim().toLowerCase() === normalized);
      if (existing) {
        return prev.filter((v) => String(v).trim().toLowerCase() !== normalized);
      }
      return [...prev, key];
    });
  };

  const renderReviewFilterGroupHeader = (label, filterKey, expanded, setExpanded, expandAriaLabel) => (
    <ListSubheader
      disableSticky
      sx={{
        fontSize: '0.72rem',
        color: 'text.secondary',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.25,
        lineHeight: 1.4,
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <Checkbox
          checked={isReviewedFilterSelected(filterKey)}
          size="small"
          sx={{ py: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            toggleReviewedFilter(filterKey);
          }}
        />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            toggleReviewedFilter(filterKey);
          }}
        >
          {label}
        </Typography>
      </Box>
      <IconButton
        size="small"
        sx={{ p: 0.25 }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((prev) => !prev);
        }}
        aria-label={expandAriaLabel}
      >
        {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </ListSubheader>
  );

  const productImageUrlByTitle = useMemo(() => {
    const map = new Map();
    (dateFilteredData || []).forEach((item) => {
      const title = item?.title;
      if (!title || map.has(title)) return;
      const url = resolveProductImageUrlFromFields(
        item.image,
        item.box_image_urls,
        item.image_urls,
        item.media_gallery_urls
      );
      if (url) map.set(title, url);
    });
    return map;
  }, [dateFilteredData]);

  const handleProductTitleClick = useCallback(
    (title, country) => {
      if (!title) return;
      const countryKey = country ? normalizeOverviewCountryKey(country) : null;
      const rows = dateFilteredData
        .filter((item) => {
          if (item?.title !== title) return false;
          if (!countryKey) return true;
          return normalizeOverviewCountryKey(item?.country) === countryKey;
        })
        .map((row) => ({
          ...row,
          category_group: getEffectiveCategoryGroup(title, row.category_group, row.original_title) || row.category_group,
        }));
      const rowTime = (item) => {
        const raw = item?.updated_at || item?.date;
        if (!raw) return 0;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
      };
      const primary = rows.length
        ? rows.reduce((best, row) => (rowTime(row) > rowTime(best) ? row : best), rows[0])
        : null;
      const productImage =
        productImageUrlByTitle.get(title) ||
        (primary
          ? resolveProductImageUrlFromFields(
              primary.image,
              primary.box_image_urls,
              primary.image_urls,
              primary.media_gallery_urls
            )
          : null);

      setProductPopupData({
        productName: title,
        productImage,
        productData: primary,
        productDataRows: rows,
        groupProductNames: undefined,
        pricingAsOfDate: dateRange[1]?.isValid?.() ? dateRange[1].format('YYYY-MM-DD') : undefined,
      });
      setProductPopupOpen(true);
    },
    [dateFilteredData, productImageUrlByTitle, categoryOverrides, dateRange]
  );
  const DEFAULT_CATEGORY_GROUPS = [
    'Devices',
    'Sticks',
    'Device Bundles',
    'Sticks Bundles',
    'Accessories',
    'Gift Cards',
    'Other Nicotine Products',
    'Other',
    'Unknown',
  ];
  const categoryGroups = useMemo(() => {
    const fromOriginal = uniqueProducts.map((p) => p.original_category_group).filter(Boolean);
    const fromOverrides = Object.values(categoryOverrides).filter(Boolean);
    return [...new Set([...DEFAULT_CATEGORY_GROUPS, ...fromOriginal, ...fromOverrides])]
      .filter((c) => c !== 'Starter Kits')
      .sort();
  }, [uniqueProducts, categoryOverrides]);

  const columns = [
    {
      field: 'title',
      headerName: 'Product Title',
      width: 420,
      minWidth: 220,
      resizable: true,
      renderCell: (params) => (
        <Box sx={{ maxWidth: '100%', display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <SkuTitleWithTranslate
              title={params.row.title}
              countriesField={params.row.countries}
              getDisplayTitle={getSkuDisplayTitle}
              isTranslationActive={isSkuTranslationActive}
              isTranslatingTitle={isSkuTitleTranslating}
              onToggleTranslate={toggleSkuTitleTranslation}
              onTitleClick={() => handleProductTitleClick(params.row.title, params.row.country)}
            />
          </Box>
          {isEditMode && params.row.hasDuplicateSkuTitle && (
            <Chip
              icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
              label={`${params.row.listingUrls?.length || 0} URLs`}
              size="small"
              color="warning"
              onClick={(e) => {
                e.stopPropagation();
                setDuplicateDialogProduct(params.row);
              }}
              sx={{ fontSize: '0.7rem', height: 22, flexShrink: 0 }}
            />
          )}
          {isEditMode && params.row.titleOverridden && !params.row.hasDuplicateSkuTitle && (
            <Chip
              icon={<EditIcon sx={{ fontSize: 14 }} />}
              label="Renamed"
              size="small"
              color="success"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setDuplicateDialogProduct(params.row);
              }}
              sx={{ fontSize: '0.7rem', height: 22, flexShrink: 0 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 72,
      minWidth: 56,
      resizable: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'first_seen_date',
      headerName: 'First Seen Date',
      width: 120,
      minWidth: 110,
      resizable: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'last_seen_date',
      headerName: 'Last Seen Date',
      width: 120,
      minWidth: 110,
      resizable: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'brand',
      headerName: 'Brand',
      width: 40,
      resizable: true,
      renderCell: (params) => {
        const isOverridden = params.row.title && brandOverrides[params.row.title] !== undefined;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isEditMode && isOverridden && (
              <EditIcon sx={{ fontSize: 14, color: '#2e7d32', flexShrink: 0 }} />
            )}
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              {params.value || '—'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'recategorized_category_group',
      headerName: 'Category Group',
      width: 120,
      resizable: true,
      renderCell: (params) => {
        const effective = getEffectiveCategoryGroup(params.row.title, params.row.original_category_group, params.row.original_title);
        const isOverridden = Boolean(getOverrideValue(categoryOverrides, params.row.title, params.row.original_title));
        const isSaving = savingTitle === params.row.title;

        if (isEditMode) {
          return (
            <Select
              size="small"
              value={effective || ''}
              onChange={(e) => handleRecategorize(params.row.title, e.target.value)}
              disabled={isSaving}
              displayEmpty
              sx={{ minWidth: 120, fontSize: '0.75rem', height: 28 }}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              {categoryGroups.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isEditMode && isOverridden && (
              <EditIcon sx={{ fontSize: 14, color: '#2e7d32', flexShrink: 0 }} />
            )}
            <Chip
              label={effective || '—'}
              size="small"
              color={isEditMode && isOverridden ? 'success' : 'default'}
              variant={isEditMode && isOverridden ? 'filled' : 'outlined'}
              sx={{ fontSize: '0.75rem' }}
            />
          </Box>
        );
      },
    },
    {
      field: 'sku_visibility',
      headerName: PRODUCT_FLAG_LABEL_SKU_VISIBILITY,
      width: 160,
      minWidth: 140,
      resizable: true,
      renderCell: (params) => {
        const val = normalizeSkuVisibility(params.value);
        if (isEditMode) {
          return (
            <FormControl size="small" sx={{ minWidth: 150 }} onClick={(e) => e.stopPropagation()}>
              <Select
                value={val || SKU_VISIBILITY_DEFAULT}
                onChange={(e) => handleSetSkuVisibility(params.row.title, e.target.value)}
                disabled={savingSkuVisibilityTitle === params.row.title}
                sx={{ fontSize: '0.75rem', height: 28 }}
              >
                {SKU_VISIBILITY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        return (
          <Chip
            label={getSkuVisibilityLabel(val)}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              ...(val === 'registered'
                ? { borderColor: '#3949ab', color: '#283593', bgcolor: '#e8eaf6' }
                : val === 'metadata'
                  ? { borderColor: '#f9a825', color: '#f57f17', bgcolor: '#fff8e1' }
                  : {}),
            }}
          />
        );
      },
    },
    {
      field: 'matching_group',
      headerName: 'Group from Matching',
      width: 120,
      resizable: true,
      renderCell: (params) => (
        <Box sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
          {params.value ?? '—'}
        </Box>
      ),
    },
    {
      field: 'is_starter_kit',
      headerName: 'Starter Kit',
      width: 40,
      resizable: true,
      type: 'boolean',
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      ),
    },
    {
      field: 'portfolioType',
      headerName: 'Limited edition',
      width: 50,
      resizable: true,
      renderCell: (params) => {
        const val = params.value;
        if (isEditMode) {
          return (
            <FormControl size="small" sx={{ minWidth: 85 }} onClick={(e) => e.stopPropagation()}>
              <Select
                value={val || ''}
                displayEmpty
                onChange={(e) => handleSetPortfolioType(params.row.title, e.target.value || null)}
                disabled={savingPortfolioTitle === params.row.title}
                sx={{ fontSize: '0.75rem', height: 28 }}
              >
                <MenuItem value="">—</MenuItem>
                <MenuItem value="Limited edition">Limited edition</MenuItem>
              </Select>
            </FormControl>
          );
        }
        return (
          <Chip
            label={val || '—'}
            size="small"
            color={val ? 'primary' : 'default'}
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        );
      },
    },
    {
      field: 'is_promo_offer',
      headerName: 'Promo offer',
      width: 50,
      resizable: true,
      renderCell: (params) => {
        const val = params.value === true;
        if (isEditMode) {
          return (
            <FormControl size="small" sx={{ minWidth: 72 }} onClick={(e) => e.stopPropagation()}>
              <Select
                value={val ? 'yes' : ''}
                displayEmpty
                onChange={(e) => handleSetPromoOffer(params.row.title, e.target.value === 'yes')}
                disabled={savingPromoOfferTitle === params.row.title}
                sx={{ fontSize: '0.75rem', height: 28 }}
              >
                <MenuItem value="">—</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </Select>
            </FormControl>
          );
        }
        return (
          <Chip
            label={val ? 'Yes' : 'No'}
            size="small"
            color={val ? 'secondary' : 'default'}
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        );
      },
    },
    {
      field: 'is_for_new_users_only',
      headerName: PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY,
      width: 56,
      resizable: true,
      renderCell: (params) => {
        const val = params.value === true;
        if (isEditMode) {
          return (
            <FormControl size="small" sx={{ minWidth: 72 }} onClick={(e) => e.stopPropagation()}>
              <Select
                value={val ? 'yes' : ''}
                displayEmpty
                onChange={(e) => handleSetForNewUsersOnly(params.row.title, e.target.value === 'yes')}
                disabled={savingForNewUsersTitle === params.row.title}
                sx={{ fontSize: '0.75rem', height: 28 }}
              >
                <MenuItem value="">—</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </Select>
            </FormControl>
          );
        }
        return (
          <Chip
            label={val ? 'Yes' : 'No'}
            size="small"
            color={val ? 'info' : 'default'}
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              ...(val
                ? { borderColor: '#00838f', color: '#006064', bgcolor: '#e0f7fa' }
                : {}),
            }}
          />
        );
      },
    },
    {
      field: 'number_of_units',
      headerName: 'Units',
      width: 70,
      resizable: true,
      renderCell: (params) => {
        const title = params.row.title;
        const displayValue = params.value ?? '—';
        if (canEditUnits) {
          const firstRow = dateFilteredData.find((r) => r.title === title);
          const effectiveValue = getEffectivePackSize(title, firstRow);
          const isSaving = savingPackSizeTitle === title;
          return (
            <TextField
              key={`units-${title}-${effectiveValue ?? 'none'}`}
              size="small"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              defaultValue={effectiveValue ?? ''}
              placeholder="—"
              disabled={isSaving}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== String(effectiveValue ?? '')) handleSavePackSize(title, v);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.target.blur();
              }}
              onClick={(e) => e.stopPropagation()}
              sx={{ width: 56, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' } }}
            />
          );
        }
        return displayValue;
      },
    },
    {
      field: 'content',
      headerName: 'Content',
      width: 50,
      minWidth: 50,
      maxWidth: 80,
      resizable: true,
      renderCell: (params) => {
        const isBundle = params.row.isDeviceOrSticksBundle;
        const effectiveCat = getEffectiveCategoryGroup(params.row.title, params.row.original_category_group, params.row.original_title);
        const options = effectiveCat === 'Sticks Bundles' ? sticksGroupsOptions : deviceBundlesContentOptions;

        if (isEditMode && isBundle) {
          const list = params.row.bundleContentList || [];
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', minWidth: 140 }} onClick={(e) => e.stopPropagation()}>
              {list.map((item, idx) => (
                <Chip
                  key={`${item}-${idx}`}
                  label={item}
                  size="small"
                  onDelete={() => {
                    const next = list.filter((_, i) => i !== idx);
                    handleSaveBundleContent(params.row.title, next);
                  }}
                  disabled={savingContentTitle === params.row.title}
                  sx={{ fontSize: '0.7rem', maxWidth: 90 }}
                />
              ))}
              <Button
                size="small"
                color="primary"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setAddContentMenuAnchor(e.currentTarget);
                  setAddContentRowTitle(params.row.title);
                  setAddContentOptions(options);
                }}
                disabled={savingContentTitle === params.row.title}
                sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
              >
                Add
              </Button>
            </Box>
          );
        }

        return (
          <Box sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 50 }}>
            {params.value ?? '—'}
          </Box>
        );
      },
    },
    {
      field: 'reviewed',
      headerName: 'Reviewed',
      width: 120,
      minWidth: 100,
      resizable: true,
      renderCell: (params) => {
        const title = params.row.title;
        const reviewers = productReviews[title] || [];
        const uncertainBy = getUncertainBy(productFlags, title);
        const isSaving = savingReviewTitle === title;
        const isReviewedByMe = userEmail && reviewers.some((e) => e.toLowerCase() === userEmail.toLowerCase());
        const isUncertainByMe = userEmail && uncertainBy.some((e) => e.toLowerCase() === userEmail.toLowerCase());
        const hasReviewers = reviewers.length > 0;
        const hasUncertain = uncertainBy.length > 0;

        const uncertainSection = (
          <>
            {uncertainBy
              .filter((email) => !userEmail || email.toLowerCase() !== userEmail.toLowerCase())
              .map((email) => (
              <Tooltip key={`uncertain-${email}`} title={`Uncertain: ${email}`}>
                <HelpOutlineIcon
                  fontSize="small"
                  sx={{ color: 'info.main', fontSize: 18 }}
                />
              </Tooltip>
            ))}
            {hasUncertain && !hasReviewers && (
              <Tooltip title="Click to approve">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleToggleReview(title); }}
                    disabled={isSaving || !userEmail}
                    sx={{ p: 0.25 }}
                    aria-label="Add review"
                  >
                    {isSaving ? <CircularProgress size={18} color="primary" /> : <AddIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={isUncertainByMe ? 'Click to remove uncertain mark' : 'Mark as uncertain'}>
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleToggleUncertain(title); }}
                  disabled={isSaving || !userEmail}
                  sx={{ color: isUncertainByMe ? 'info.main' : 'text.secondary', p: 0.25 }}
                  aria-label={isUncertainByMe ? 'Remove uncertain mark' : 'Mark as uncertain'}
                >
                  {isSaving && !hasReviewers && !hasUncertain ? (
                    <CircularProgress size={18} color="primary" />
                  ) : (
                    <HelpOutlineIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        );

        const reviewIcons = hasReviewers ? (
          <>
            {reviewers.map((email) => {
              const isDominik = (email?.trim().toLowerCase() || '') === ADMIN_EDIT_EMAIL.toLowerCase();
              return (
                <Tooltip key={email} title={email}>
                  {isDominik ? (
                    <VerifiedIcon
                      fontSize="small"
                      sx={{ color: 'primary.main', fontSize: 18 }}
                    />
                  ) : (
                    <CheckCircleIcon
                      fontSize="small"
                      sx={{ color: 'success.main', fontSize: 18 }}
                    />
                  )}
                </Tooltip>
              );
            })}
            <Tooltip title={isReviewedByMe ? 'Click to remove your review' : 'Click to add your review'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleToggleReview(title)}
                  disabled={isSaving || !userEmail}
                  sx={{ p: 0.25 }}
                  aria-label={isReviewedByMe ? 'Remove review' : 'Add review'}
                >
                  {isSaving ? <CircularProgress size={18} color="primary" /> : isReviewedByMe ? <RemoveIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : !hasUncertain ? (
          <Tooltip title="Waiting for review – click to approve">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleToggleReview(title); }}
              disabled={isSaving || !userEmail}
              sx={{ color: '#f9a825', p: 0.25 }}
              aria-label="Waiting for review"
            >
              {isSaving ? <CircularProgress size={18} color="primary" /> : <ScheduleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        ) : null;

        const incorrectInfoReportedBy = getIncorrectInfoReportedBy(productFlags, title);
        const isIncorrectInfoReportedByMe =
          userEmail && incorrectInfoReportedBy.some((e) => e.toLowerCase() === userEmail.toLowerCase());
        const othersReportedIncorrectInfo = incorrectInfoReportedBy.filter(
          (email) => !userEmail || email.toLowerCase() !== userEmail.toLowerCase()
        );
        const productTickets = flagTicketsByTitle[title] || [];
        const flagTone = getFlagTicketTone(productTickets);
        const hasFlagTickets = productTickets.length > 0;
        const showFilledFlag = Boolean(isIncorrectInfoReportedByMe || hasFlagTickets);
        const flagTooltip = flagTone === 'pending'
          ? 'Open support ticket — click to view or report again'
          : flagTone === 'resolved'
            ? 'All support tickets resolved/closed — click to report again'
            : isIncorrectInfoReportedByMe
              ? 'Support ticket submitted — click to report again'
              : othersReportedIncorrectInfo.length > 0
                ? `Flag incorrect information (${othersReportedIncorrectInfo.length} report${othersReportedIncorrectInfo.length === 1 ? '' : 's'} from others)`
                : 'Flag incorrect information';
        const flagToneSx = flagTone === 'resolved'
          ? {
              bgcolor: 'success.light',
              color: 'success.dark',
              borderRadius: 1,
              '&:hover': { bgcolor: 'success.main', color: 'success.contrastText' },
            }
          : flagTone === 'pending' || isIncorrectInfoReportedByMe
            ? {
                bgcolor: 'warning.light',
                color: 'warning.dark',
                borderRadius: 1,
                '&:hover': { bgcolor: 'warning.main', color: 'warning.contrastText' },
              }
            : {};

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
            {uncertainSection}
            {reviewIcons}
            <Tooltip title={flagTooltip}>
              <IconButton
                size="small"
                color={flagTone === 'resolved' ? 'success' : 'warning'}
                onClick={(e) => { e.stopPropagation(); handleOpenFlagDialog(params.row); }}
                sx={{
                  p: 0.25,
                  ...flagToneSx,
                }}
                aria-label={showFilledFlag ? 'Incorrect info reported' : 'Flag incorrect'}
              >
                {showFilledFlag ? (
                  <FlagIcon fontSize="small" />
                ) : (
                  <OutlinedFlagIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'support_ticket_descriptions',
      headerName: 'Support ticket descriptions',
      width: 280,
      minWidth: 180,
      wrap: true,
      renderCell: (params) => {
        const tickets = flagTicketsByTitle[params.row.title] || [];
        const text = formatConcatenatedTicketDescriptions(tickets);
        if (!text) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              —
            </Typography>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.35,
            }}
          >
            {text}
          </Typography>
        );
      },
    },
    {
      field: 'support_ticket_status',
      headerName: 'Support ticket status',
      width: 200,
      minWidth: 160,
      wrap: true,
      renderCell: (params) => {
        const title = params.row.title;
        const tickets = flagTicketsByTitle[title] || [];
        if (!tickets.length) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              —
            </Typography>
          );
        }
        return (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          >
            {tickets.map((ticket) => (
              <Box key={ticket.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                {tickets.length > 1 && (
                  <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>
                    #{ticket.id}
                  </Typography>
                )}
                {canChangeTicketStatus ? (
                  <FormControl size="small" sx={{ minWidth: 140, flex: 1 }} onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={ticket.status || 'Open'}
                      onChange={(e) => handleFlagTicketStatusChange(ticket.id, e.target.value, title)}
                      disabled={savingTicketStatusId === ticket.id}
                      sx={{ fontSize: '0.75rem', height: 28 }}
                    >
                      {TICKET_STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Chip
                    size="small"
                    label={ticket.status || 'Open'}
                    color={getTicketStatusColor(ticket.status)}
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                )}
                {savingTicketStatusId === ticket.id && <CircularProgress size={14} />}
              </Box>
            ))}
          </Box>
        );
      },
    },
  ];

  const visibleColumns = columns.filter((col) => !hiddenColumns.has(col.field));

  const toggleColumnVisibility = (field) => {
    if (ALWAYS_VISIBLE_COLUMNS.has(field)) return;
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      try {
        localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        // ignore quota / private mode
      }
      return next;
    });
  };

  if (loading) {
    return (
      <ThemeProvider theme={viewTheme}>
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress color="primary" />
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={viewTheme}>
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={viewTheme}>
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4">
          Products Mapping Overview
        </Typography>
        {isEditMode && (
          <Chip
            label="Edit mode"
            size="small"
            color="primary"
            icon={<EditIcon sx={{ fontSize: 16 }} />}
          />
        )}
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 700 }}>
        Review and map the product catalogue across markets — categories, matching groups, variants, and review status.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 2,
            }}
          >
            <DateRangeFilter
              availableDates={availableDates}
              effectiveDateRange={effectiveDateRange}
              dateRange={dateRange}
              setDateRange={setDateRange}
              setPreviewDateRange={setPreviewDateRange}
              dateSliderValue={dateSliderValue}
              onSliderChange={handleDateSliderChange}
              onSliderCommit={handleDateSliderCommit}
              accentColor={styleTokens.accent}
            />
            <OverviewProductSearchField
              onSearch={handleProductSearchSubmit}
              isSearching={isProductSearchFiltering}
              accentColor={styleTokens.accent}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Brand</InputLabel>
              <Select
                value={filterBrand}
                label="Brand"
                onChange={(e) => setFilterBrand(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {brands.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category Group</InputLabel>
              <Select
                value={filterRecategorized}
                label="Category Group"
                onChange={(e) => setFilterRecategorized(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {categoryGroups.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Country</InputLabel>
              <Select
                value={filterCountry}
                label="Country"
                onChange={(e) => setFilterCountry(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="reviewed-filter-label">Reviewed</InputLabel>
              <Select
                labelId="reviewed-filter-label"
                multiple
                value={filterReviewedBy}
                label="Reviewed"
                onChange={(e) => {
                  const value = e.target.value;
                  setFilterReviewedBy(typeof value === 'string' ? value.split(',') : value);
                }}
                input={<OutlinedInput label="Reviewed" />}
                renderValue={(selected) =>
                  selected.length === 0
                    ? 'All'
                    : selected.map(formatReviewedFilterLabel).join(', ')
                }
                MenuProps={{
                  PaperProps: { style: { maxHeight: 360, width: 320 } },
                  autoFocus: false,
                }}
              >
                {renderReviewFilterGroupHeader(
                  'Not reviewed by',
                  NOT_REVIEWED_FILTER_KEY,
                  notReviewedByExpanded,
                  setNotReviewedByExpanded,
                  notReviewedByExpanded ? 'Collapse Not reviewed by' : 'Expand Not reviewed by'
                )}
                {notReviewedByExpanded &&
                  notReviewedByEmails.map((email) => (
                    <MenuItem
                      key={`not-reviewed-filter-${email}`}
                      value={notReviewedEmailToFilterKey(email)}
                      dense
                      sx={{ pl: 4 }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleReviewedFilter(notReviewedEmailToFilterKey(email));
                      }}
                    >
                      <Checkbox
                        checked={isReviewedFilterSelected(notReviewedEmailToFilterKey(email))}
                        size="small"
                        sx={{ py: 0 }}
                      />
                      <ListItemText primary={email} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
                <Divider sx={{ my: 0.5 }} />
                {renderReviewFilterGroupHeader(
                  'Uncertain',
                  UNCERTAIN_FILTER_KEY,
                  uncertainReviewersExpanded,
                  setUncertainReviewersExpanded,
                  uncertainReviewersExpanded ? 'Collapse Uncertain' : 'Expand Uncertain'
                )}
                {uncertainReviewersExpanded &&
                  uncertainFlaggerEmails.map((email) => (
                    <MenuItem
                      key={`uncertain-filter-${email}`}
                      value={uncertainEmailToFilterKey(email)}
                      dense
                      sx={{ pl: 4 }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleReviewedFilter(uncertainEmailToFilterKey(email));
                      }}
                    >
                      <Checkbox
                        checked={isReviewedFilterSelected(uncertainEmailToFilterKey(email))}
                        size="small"
                        sx={{ py: 0 }}
                      />
                      <ListItemText primary={email} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
                <Divider sx={{ my: 0.5 }} />
                {renderReviewFilterGroupHeader(
                  'Reviewed',
                  REVIEWED_FILTER_KEY,
                  reviewedReviewersExpanded,
                  setReviewedReviewersExpanded,
                  reviewedReviewersExpanded ? 'Collapse Reviewed' : 'Expand Reviewed'
                )}
                {reviewedReviewersExpanded &&
                  otherReviewerEmails.map((email) => (
                    <MenuItem
                      key={`reviewed-filter-${email}`}
                      value={email}
                      dense
                      sx={{ pl: 4 }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleReviewedFilter(email);
                      }}
                    >
                      <Checkbox checked={isReviewedFilterSelected(email)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={email} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
                {reviewedReviewersExpanded && forloopReviewerEmails.length > 0 && (
                  <>
                    <ListSubheader
                      disableSticky
                      sx={{
                        fontSize: '0.72rem',
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.25,
                        lineHeight: 1.4,
                        pl: 4,
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <Checkbox
                          checked={isReviewedFilterSelected(FORLOOP_TEAM_FILTER_KEY)}
                          size="small"
                          sx={{ py: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReviewedFilter(FORLOOP_TEAM_FILTER_KEY);
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReviewedFilter(FORLOOP_TEAM_FILTER_KEY);
                          }}
                        >
                          Forloop team
                        </Typography>
                      </Box>
                    </ListSubheader>
                    {forloopReviewerEmails.map((email) => (
                      <MenuItem
                        key={`reviewed-forloop-${email}`}
                        value={email}
                        dense
                        sx={{ pl: 6 }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleReviewedFilter(email);
                        }}
                      >
                        <Checkbox checked={isReviewedFilterSelected(email)} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={email} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                  </>
                )}
              </Select>
            </FormControl>
            {isEditMode && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filterDuplicatesOnly}
                    onChange={(e) => setFilterDuplicatesOnly(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2">Duplicate SKU titles</Typography>
                    {duplicateSkuCount > 0 && (
                      <Chip label={duplicateSkuCount} size="small" color="warning" sx={{ height: 20 }} />
                    )}
                  </Box>
                }
                sx={{ ml: 0, mr: 0 }}
              />
            )}
            <Tooltip title="Show or hide columns">
              <IconButton
                size="small"
                aria-label="Column settings"
                onClick={(e) => setColumnSettingsAnchor(e.currentTarget)}
                sx={{ color: Boolean(columnSettingsAnchor) ? 'primary.main' : 'text.secondary' }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={columnSettingsAnchor}
              open={Boolean(columnSettingsAnchor)}
              onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                  setColumnSettingsAnchor(null);
                }
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { maxHeight: 420, minWidth: 240 } }}
            >
              <ListSubheader sx={{ lineHeight: 2, fontSize: '0.75rem' }}>Visible columns</ListSubheader>
              {columns.map((col) => {
                const locked = ALWAYS_VISIBLE_COLUMNS.has(col.field);
                const checked = !hiddenColumns.has(col.field);
                return (
                  <MenuItem
                    key={col.field}
                    dense
                    disabled={locked}
                    onClick={() => toggleColumnVisibility(col.field)}
                  >
                    <Checkbox checked={checked} size="small" disabled={locked} sx={{ py: 0 }} />
                    <ListItemText primary={col.headerName} sx={{ my: 0 }} />
                  </MenuItem>
                );
              })}
            </Menu>
            <Typography variant="body2" color="text.secondary">
              {filteredProducts.length} of {uniqueProducts.length} products
            </Typography>
          </Box>

          {isEditMode && duplicateSkuCount > 0 && !filterDuplicatesOnly && (
            <Alert
              severity="warning"
              sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
              action={
                <Button color="inherit" size="small" onClick={() => setFilterDuplicatesOnly(true)}>
                  Show duplicates
                </Button>
              }
            >
              {duplicateSkuCount} product{duplicateSkuCount === 1 ? '' : 's'} share the same title on the same date in the same country with different URLs.
            </Alert>
          )}

          {showEmptyDateRangeSuggestion && (
            <Alert
              severity="info"
              sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Typography variant="body2" sx={{ mr: 0.5 }}>
                  No products for this date range. Adjust the datetime filter — expand by:
                </Typography>
                {[1, 7, 30].map((days) => (
                  <Button
                    key={days}
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => expandDateRangeByDays(days)}
                    sx={{
                      textTransform: 'none',
                      borderColor: 'info.main',
                      color: 'info.dark',
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'info.50', borderColor: 'info.dark' },
                    }}
                  >
                    +{days} {days === 1 ? 'day' : 'days'}
                  </Button>
                ))}
              </Box>
            </Alert>
          )}

          <Box sx={{ width: '100%' }}>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {visibleColumns.map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            minWidth: col.minWidth ?? col.width ?? 80,
                          }}
                        >
                          {col.headerName}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedProducts.map((row) => (
                      <TableRow key={row.id} hover>
                        {visibleColumns.map((col) => {
                            const params = { value: row[col.field], row };
                            const cellContent = col.renderCell
                              ? col.renderCell(params)
                              : (row[col.field] ?? '—');
                            return (
                              <TableCell
                                key={col.field}
                                sx={{
                                  fontSize: '0.875rem',
                                  verticalAlign: col.wrap ? 'top' : 'middle',
                                  maxWidth: col.width ?? 200,
                                  overflow: col.wrap ? 'visible' : 'hidden',
                                  textOverflow: col.wrap ? 'clip' : 'ellipsis',
                                  whiteSpace: col.wrap ? 'normal' : undefined,
                                }}
                              >
                                {cellContent}
                              </TableCell>
                            );
                          })}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredProducts.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          </Box>
          <Menu
            anchorEl={addContentMenuAnchor}
            open={Boolean(addContentMenuAnchor)}
            onClose={() => { setAddContentMenuAnchor(null); setAddContentRowTitle(null); setAddContentOptions([]); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            {addContentOptions.map((opt) => (
              <MenuItem
                key={opt}
                onClick={() => {
                  const current = addContentRowTitle ? (bundleContentOverrides[addContentRowTitle] || []) : [];
                  handleSaveBundleContent(addContentRowTitle, [...current, opt]);
                  setAddContentMenuAnchor(null);
                  setAddContentRowTitle(null);
                  setAddContentOptions([]);
                }}
              >
                {opt}
              </MenuItem>
            ))}
          </Menu>

          <Dialog open={flagDialogOpen} onClose={handleCloseFlagDialog} maxWidth="md" fullWidth>
            <DialogTitle>Flag incorrect information</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Report incorrect or misleading information for this product. A support ticket will be created.
              </Typography>
              {flagProduct && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {flagProduct.title}
                  </Typography>
                  {existingFlagTicketsLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Checking existing tickets…
                      </Typography>
                    </Box>
                  )}
                  {!existingFlagTicketsLoading && existingFlagTickets.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {existingFlagTickets.some((t) => t.is_mine)
                        ? 'You already submitted a support ticket for this product. You can send another report if needed.'
                        : 'A support ticket already exists for this product. You can send another report if needed.'}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                        {existingFlagTickets.map((ticket) => (
                          <FlagTicketListItem
                            key={ticket.id}
                            ticket={ticket}
                            projectKey={projectKey}
                            canChangeStatus={canChangeTicketStatus}
                            discussionOpen={Boolean(expandedFlagTickets[ticket.id]?.discussion)}
                            onToggleDiscussion={() => toggleFlagTicketDiscussion(ticket.id)}
                            details={flagTicketDetails[ticket.id]}
                            savingStatus={savingTicketStatusId === ticket.id}
                            onStatusChange={handleFlagTicketStatusChange}
                          />
                        ))}
                      </Box>
                    </Alert>
                  )}
                  {!existingFlagTicketsLoading &&
                    existingFlagTickets.length === 0 &&
                    userEmail &&
                    getIncorrectInfoReportedBy(productFlags, flagProduct.title).some(
                      (e) => e.toLowerCase() === userEmail.toLowerCase()
                    ) && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        You already submitted a support ticket for this product. You can send another report if needed.
                      </Alert>
                    )}
                </>
              )}
              <TextField
                autoFocus
                fullWidth
                multiline
                rows={3}
                label="Description (optional)"
                placeholder="Describe what is incorrect..."
                value={flagDescription}
                onChange={(e) => setFlagDescription(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseFlagDialog} disabled={flagSubmitting}>
                Cancel
              </Button>
              <Button variant="contained" color="warning" onClick={handleSubmitFlag} disabled={flagSubmitting}>
                {flagSubmitting ? 'Creating...' : 'Create support ticket'}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={flagSnackbar.open}
            autoHideDuration={4000}
            onClose={() => setFlagSnackbar((s) => ({ ...s, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setFlagSnackbar((s) => ({ ...s, open: false }))}
              severity={flagSnackbar.severity}
              variant="filled"
            >
              {flagSnackbar.message}
            </Alert>
          </Snackbar>
        </CardContent>
      </Card>

      <SkuDuplicateRenameDialog
        open={Boolean(duplicateDialogProduct)}
        product={duplicateDialogProduct}
        titleOverrides={titleOverrides}
        existingTitlesInCountry={
          duplicateDialogProduct
            ? uniqueProducts
                .filter((p) => p.country === duplicateDialogProduct.country)
                .map((p) => p.title)
            : []
        }
        savingUrl={savingRenameUrl}
        onClose={() => setDuplicateDialogProduct(null)}
        onRename={handleRenameProductByUrl}
        onRevert={handleRevertProductTitleByUrl}
      />

      <ProductDetailPopup
        open={productPopupOpen}
        onClose={() => setProductPopupOpen(false)}
        productName={productPopupData.productName}
        productImage={productPopupData.productImage}
        productData={productPopupData.productData}
        productDataRows={productPopupData.productDataRows}
        groupProductNames={productPopupData.groupProductNames}
        packSizeOverrides={packSizeOverrides}
        productFlags={productFlags}
        pricingAsOfDate={productPopupData.pricingAsOfDate}
        projectKey={projectKey || 'jti-global'}
        country={productPopupData.productData?.country}
        accentColor={styleTokens.accent}
        getDisplayTitle={getSkuDisplayTitle}
        isTranslationActive={isSkuTranslationActive}
        isTranslatingTitle={isSkuTitleTranslating}
        onToggleTranslate={toggleSkuTitleTranslation}
      />
    </Box>
    </ThemeProvider>
  );
}

export default JTIProductsOverviewView;
